import { Helmet } from "react-helmet-async";
import { serializeJsonLd } from "./jsonLd";

const SITE_URL = "https://www.zetech.ac.ke";

/**
 * The key public destinations a searcher should be able to jump to
 * straight from the search result — mirrors the header navigation and
 * the commercial programme sub-categories (diploma, certificate,
 * degree, masters, PhD, TVET) plus fees, applying, blog and events.
 *
 * Every entry is a real, indexable route with its own title,
 * description and canonical — never a filter state or a hash link.
 */
export const SITE_NAVIGATION = [
  { name: "Programmes", path: "/programmes" },
  { name: "Diploma Courses", path: "/programmes/qualification/diploma" },
  { name: "Certificate Courses", path: "/programmes/qualification/certificate" },
  { name: "Degree Programmes", path: "/programmes/qualification/degree" },
  { name: "Masters Programmes", path: "/programmes/qualification/masters" },
  { name: "PhD Programmes", path: "/programmes/qualification/phd" },
  { name: "TVET Courses", path: "/programmes/qualification/tvet" },
  { name: "Tuition Fees", path: "/tuition-fees" },
  { name: "Apply Online", path: "/application-process" },
  { name: "Blog", path: "/blog" },
  { name: "Events", path: "/events" },
  { name: "Student Life", path: "/students-life" },
  { name: "Student Forms", path: "/student-forms" },
  { name: "About Zetech University", path: "/about-zu" },
];

/**
 * Sitewide WebSite + SiteNavigationElement structured data.
 *
 * Every page's WebPage node already says isPartOf: #website — but that
 * node was never actually defined anywhere, so crawlers followed a
 * dangling reference. This component defines it once (rendered from
 * <Header /> next to <OrganizationSchema />) and attaches the site's
 * navigation graph as SiteNavigationElement entries. That navigation
 * graph is the documented schema.org signal Google and Bing use to
 * understand which sections matter most — the same structure behind
 * the "Diploma Courses / Certificate Courses / Degree Programmes"
 * deep links shown under a homepage result.
 */
export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "Zetech University",
      alternateName: ["Zetech", "Zetech University Kenya"],
      inLanguage: "en-KE",
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": "ItemList",
      "@id": `${SITE_URL}/#site-navigation`,
      name: "Zetech University — Main Navigation",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: SITE_NAVIGATION.length,
      itemListElement: SITE_NAVIGATION.map((item, index) => ({
        "@type": "SiteNavigationElement",
        position: index + 1,
        name: item.name,
        url: `${SITE_URL}${item.path}`,
      })),
    },
  ],
};

export default function WebSiteSchema() {
  const json = serializeJsonLd(WEBSITE_SCHEMA);
  if (!json) return null;

  return (
    <Helmet>
      <script type="application/ld+json">{json}</script>
    </Helmet>
  );
}
